/**
 * What a single Esc keypress should back out of in the form builder.
 *
 * The canvas can hold four independent "pending" states at once, and QA's
 * complaint was that Esc only ever cleared one of them (an armed move) — arming
 * a palette card or selecting a field left the originator with no keyboard way
 * out, which is a WCAG 2.1.2 (No Keyboard Trap) problem as much as an
 * ergonomic one.
 *
 * The rule is that Esc backs out ONE level at a time, most-transient first, so
 * repeated presses walk the user back out of the builder's state rather than
 * clearing everything at once. Arming is more transient than a selection, so
 * Esc disarms before it deselects: from "field selected AND palette armed",
 * the first press disarms the palette and the second closes the properties
 * rail — the selection the user was working against survives the press that
 * cancelled the thing they just started.
 *
 * Kept as a pure function (like `resolveDrop` in builderDnd) so the priority
 * order is unit-testable without mounting the builder or faking key events.
 */
export type EscapeState = {
  /** A pointer drag is mid-flight. */
  dragging: boolean;
  /** A palette card is armed for click-to-place. */
  hasPendingEntry: boolean;
  /** A placed field is armed to be moved to another column. */
  hasMovingFrom: boolean;
  /** A field or a section owns the right-hand properties rail. */
  hasSelection: boolean;
};

export type EscapeAction =
  /** Do nothing — something else owns this keypress, or there is nothing to cancel. */
  | 'none'
  /** Disarm the palette card. */
  | 'clearPendingEntry'
  /** Cancel the armed move. */
  | 'clearMovingFrom'
  /** Drop the field/section selection, closing the properties rail. */
  | 'clearSelection';

export function resolveEscape(state: EscapeState): EscapeAction {
  // A drag in flight belongs to dnd-kit: AbstractPointerSensor attaches its own
  // document keydown listener and cancels the drag on Esc. Acting here too
  // would consume the same keypress twice, so aborting a drag would ALSO
  // disarm or deselect as an invisible side effect.
  if (state.dragging) return 'none';
  if (state.hasPendingEntry) return 'clearPendingEntry';
  if (state.hasMovingFrom) return 'clearMovingFrom';
  if (state.hasSelection) return 'clearSelection';
  return 'none';
}

/**
 * Whether the builder should ignore this Esc entirely and let something layered
 * on top handle it.
 *
 * Two cases, both of which would otherwise destroy state the user did not ask
 * to lose:
 *
 *  - **An open overlay.** AntD modals, drawers, selects, date pickers, popovers
 *    and dropdowns all close themselves on Esc without stopping propagation, so
 *    one press would both dismiss the overlay AND wipe the selection underneath
 *    it.
 *  - **Focus inside a text input.** Esc in a field means "I'm done with this
 *    value", not "throw away my selection" — and the properties rail is nothing
 *    but inputs, so clearing the selection would close the very rail being
 *    typed into.
 */
const OPEN_OVERLAY_SELECTOR = [
  '.ant-modal-wrap:not([style*="display: none"])',
  '.ant-drawer-open',
  '.ant-select-open',
  '.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)',
  '.ant-popover:not(.ant-popover-hidden)',
  '.ant-dropdown:not(.ant-dropdown-hidden)',
].join(', ');

export function shouldIgnoreEscape(target: EventTarget | null, doc: Document): boolean {
  if (doc.querySelector(OPEN_OVERLAY_SELECTOR)) return true;
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;
  return (
    el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.isContentEditable === true ||
    el.getAttribute?.('role') === 'combobox'
  );
}
